/**
 * Modal.jsx
 *
 * @Author: jruif
 * @Date: 2017/7/7 下午9:07
 *
 * todo: 移除Box，使用Overlay & Portal组件重写
 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { ariaHidden, ownerDocument } from './modalUtil';
import TrapFocus from './TrapFocus';
import Portal from '../portal';
import IconButton from '../button/IconButton';
import Close from '../icons/internals/Close';
import ModalManager from './ModalManager';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import ModalContent from './ModalContent';

function getContainer(container, defaultContainer) {
  container = typeof container === 'function' ? container() : container;
  return ReactDOM.findDOMNode(container) || defaultContainer;
}

/**
 * Modal
 */
class Modal extends Component {
  static defaultProps = {
    prefixCls: 'ne-modal',
    hideCloseButton: false,
    size: 'medium',
    manager: new ModalManager(),
    animationName: 'scale',
    // parentSelector: document.body,
  };

  static propTypes = {
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    /** 是否隐藏关闭按钮 */
    hideCloseButton: PropTypes.bool,
    /** 是否打开 */
    isOpen: PropTypes.bool,
    /** 动画类型 */
    animationName: PropTypes.oneOf(['scale', 'slide-up', 'slide-down']),
    /**
     * 组件尺寸
     */
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    /**
     * 是否隐藏遮罩层
     */
    hideBackdrop: PropTypes.bool,
    /**
     * 是否禁用遮罩层点击关闭
     */
    disableBackdropClick: PropTypes.bool,
    /**
     * 是否禁用esc关闭
     */
    disableEscapeKeyDown: PropTypes.bool,
    /**
     * 自定义按钮
     */
    actions: PropTypes.node,
  };

  static getDerivedStateFromProps(nextProps) {
    if (nextProps.isOpen) {
      return {
        exited: false,
      };
    }
    return null;
  }

  mounted = false;

  constructor(props) {
    super();
    this.state = {
      exited: !props.isOpen,
    };
  }

  componentDidMount() {
    this.mounted = true;
    const { isOpen } = this.props;
    if (isOpen) {
      this.handleOpen();
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props;
    if (prevProps.isOpen && !isOpen) {
      this.handleClose();
    } else if (!prevProps.isOpen && isOpen) {
      this.handleOpen();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    const { isOpen } = this.props;
    const { exited } = this.state;

    if (isOpen || !exited) {
      this.handleClose('unmount');
    }
  }

  handleOpen = () => {
    const { container, manager } = this.props;
    const doc = this.getDoc();
    const modalContainer = getContainer(container, doc.body);

    manager.add(this, modalContainer);
    if (this.modalRef) {
      this.handleOpened();
    }
  };

  handleRendered = () => {
    const { isOpen, onRendered } = this.props;
    if (onRendered) {
      onRendered();
    }

    if (isOpen) {
      this.handleOpened();
    } else {
      ariaHidden(this.modalRef, true);
    }
  };

  handleOpened = () => {
    const { manager } = this.props;
    manager.mount(this);

    // Fix a bug on Chrome where the scroll isn't initially 0.
    this.modalRef.scrollTop = 0;
  };

  handleClose = reason => {
    const { closeAfterTransition, manager } = this.props;
    /* If the component does not have a transition or is unmounting remove the Modal
    otherwise let the transition handle removing the style, this prevents elements
    moving around when the Modal is closed. */
    if (!closeAfterTransition || reason === 'unmount') {
      manager.remove(this);
    }
  };

  handleExited = () => {
    const { closeAfterTransition, manager, onExited } = this.props;
    if (onExited) {
      onExited();
    }
    if (closeAfterTransition) {
      manager.remove(this);
    }
    this.setState({ exited: true });
  };

  handleBackdropClick = event => {
    if (event.target !== event.currentTarget) {
      return;
    }

    const { onBackdropClick, disableBackdropClick, onClose } = this.props;
    if (onBackdropClick) {
      onBackdropClick(event);
    }

    if (!disableBackdropClick && onClose) {
      onClose(event, 'backdropClick');
    }
  };

  handleKeyDown = event => {
    // event.defaultPrevented:
    //
    // Ignore events that have been `event.preventDefault()` marked.
    // preventDefault() is meant to stop default behaviours like
    // clicking a checkbox to check it, hitting a button to submit a form,
    // and hitting left arrow to move the cursor in a text input etc.
    // Only special HTML elements have these default bahaviours.
    //
    // To remove in v4.
    if (event.key !== 'Escape' || !this.isTopModal()) {
      return;
    }

    // Swallow the event, in case someone is listening for the escape key on the body.
    event.stopPropagation();

    const { onEscapeKeyDown, disableEscapeKeyDown, onClose } = this.props;
    if (onEscapeKeyDown) {
      onEscapeKeyDown(event);
    }

    if (!disableEscapeKeyDown && onClose) {
      onClose(event, 'escapeKeyDown');
    }
  };

  handlePortalRef = ref => {
    this.mountNode = ref ? ref.getMountNode() : ref;
  };

  handleModalRef = ref => {
    this.modalRef = ref;
  };

  isTopModal = () => this.props.manager.isTopModal(this);

  getDoc = () => ownerDocument(this.mountNode);

  handleMouseDown = event => {
    this.mouseDownTarget = event.target;
  };

  handleBackdropClick = event => {
    // Ignore the events not coming from the "backdrop"
    // We don't want to close the dialog when clicking the dialog content.
    if (event.target !== event.currentTarget) {
      return;
    }

    // Make sure the event starts and ends on the same DOM element.
    if (event.target !== this.mouseDownTarget) {
      return;
    }
    const { onBackdropClick, disableBackdropClick, onClose } = this.props;
    this.mouseDownTarget = null;

    if (onBackdropClick) {
      onBackdropClick(event);
    }

    if (!disableBackdropClick && onClose) {
      onClose(event, 'backdropClick');
    }
  };

  handleCloseButtonClick = event => {
    const { onClose } = this.props;
    if (onClose) {
      onClose(event, 'buttonClick');
    }
  };

  render() {
    const {
      className,
      portalClassName,
      prefixCls,
      overlayClassName,
      title,
      animationName,
      actions,
      hideCloseButton,
      children,
      size,
      BackdropComponent,
      BackdropProps,
      classes,
      closeAfterTransition,
      container,
      disableAutoFocus,
      disableBackdropClick,
      disableEnforceFocus,
      disableEscapeKeyDown,
      disablePortal,
      disableRestoreFocus,
      hideBackdrop,
      keepMounted,
      manager,
      onBackdropClick,
      onClose,
      onEscapeKeyDown,
      onRendered,
      isOpen,
      ...other
    } = this.props;
    const { exited } = this.state;
    if ((!keepMounted && !isOpen && exited) || !children) {
      return null;
    }

    const closeButton = hideCloseButton ? null : (
      <IconButton
        className={`${prefixCls}-close`}
        onClick={this.handleCloseButtonClick}
        variant="text">
        <Close />
      </IconButton>
    );
    return (
      <Portal
        ref={this.handlePortalRef}
        container={container}
        disablePortal={disablePortal}
        onRendered={this.handleRendered}>
        {/*
          Marking an element with the role presentation indicates to assistive technology
          that this element should be ignored; it exists to support the web application and
          is not meant for humans to interact with directly.
          https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md
        */}
        <div
          ref={this.handleModalRef}
          onKeyDown={this.handleKeyDown}
          role="presentation"
          className={classNames(prefixCls, className, { [`${prefixCls}-hidden`]: exited })}
          {...other}>
          {hideBackdrop ? null : (
            <CSSTransition in={isOpen} timeout={500} classNames={`${prefixCls}-backdrop`} appear>
              <div
                onClick={this.handleBackdropClick}
                aria-hidden="true"
                className={`${prefixCls}-backdrop`}/>
            </CSSTransition>
          )}
          <TrapFocus
            disableEnforceFocus={disableEnforceFocus}
            disableAutoFocus={disableAutoFocus}
            disableRestoreFocus={disableRestoreFocus}
            getDoc={this.getDoc}
            isEnabled={this.isTopModal}
            isOpen={isOpen}>
            <CSSTransition
              in={isOpen}
              timeout={300}
              classNames={`${prefixCls}-container-${animationName}`}
              appear
              onExited={this.handleExited}>
              <div
                className={`${prefixCls}-container`}
                role="document"
                onClick={this.handleBackdropClick}
                onMouseDown={this.handleMouseDown}>
                <div className={classNames(`${prefixCls}-body`, `${prefixCls}-body-${size}`)}>
                  {title ? (
                    <ModalHeader closeButton={closeButton}>{title}</ModalHeader>
                  ) : (
                    closeButton
                  )}
                  {children ? <ModalContent>{children}</ModalContent> : null}
                  {actions ? <ModalFooter>{actions}</ModalFooter> : null}
                </div>
              </div>
            </CSSTransition>
          </TrapFocus>
        </div>
      </Portal>
    );
  }
}
export default Modal;



// WEBPACK FOOTER //
// ./src/modal/Modal.jsx